import { createSlice } from "@reduxjs/toolkit";
import { PayloadAction } from "@reduxjs/toolkit/dist/createAction";
import {
    asyncCreateFinanceAction,
    asyncDeleteFinanceAction, asyncGetDailyDealsCountAction,
    asyncGetFinanceAction,
    asyncGetFinancesAction,
    asyncGetFinanceStatsAction,
    asyncUpdateFinanceAction
} from "./financeAction";
import { IFinance } from "../../../helpers/types";
import { toast } from "react-toastify";
import { tostSetting } from "../../../helpers/scripts";

interface FinanceState {
    finances: IFinance[];
    totalFinances: number;
    finance: IFinance | null;
    stats: any | null;
    dailyDealsCount: any[] | null; // <-- Новое поле
    currentPage: number;
    isLoading: boolean;
    isStatsLoading: boolean;
    error: string;
}

const initialState: FinanceState = {
    finances: [],
    totalFinances: 0,
    finance: null,
    dailyDealsCount: null,
    stats: null,
    currentPage: 1,
    isLoading: false,
    isStatsLoading: false,
    error: ""
};

const financeSlice = createSlice({
    name: "financeSlice",
    initialState,
    reducers: {
        setCurrentPageFinance(state, action) {
            state.currentPage = action.payload;
        },
        resetFinance(state) {
            state = initialState;
        }
    },
    extraReducers: (builder: any) => {
        builder
            .addCase(asyncGetFinancesAction.fulfilled, (state: FinanceState, action: PayloadAction<any>) => {
                const { rows, count } = action.payload;
                state.isLoading = false;
                state.error = "";
                state.totalFinances = count;
                state.finances = rows || [];
            })
            .addCase(asyncGetFinancesAction.pending, (state: FinanceState) => {
                state.isLoading = true;
            })
            .addCase(asyncGetFinancesAction.rejected, (state: FinanceState, action: PayloadAction<any>) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(asyncGetFinanceAction.fulfilled, (state: FinanceState, action: PayloadAction<any>) => {
                state.isLoading = false;
                state.error = "";
                state.finance = action.payload;
            })
            .addCase(asyncGetFinanceAction.pending, (state: FinanceState) => {
                state.isLoading = true;
            })
            .addCase(asyncGetFinanceAction.rejected, (state: FinanceState, action: PayloadAction<any>) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(asyncCreateFinanceAction.fulfilled, (state: FinanceState, action: PayloadAction<IFinance>) => {
                state.isLoading = false;
                state.error = "";
                state.finances = [action.payload, ...state.finances];
                state.totalFinances = state.totalFinances + 1;
                toast.success("Finance record successfully created", tostSetting);
            })
            .addCase(asyncCreateFinanceAction.pending, (state: FinanceState) => {
                state.isLoading = true;
            })
            .addCase(asyncCreateFinanceAction.rejected, (state: FinanceState, action: PayloadAction<any>) => {
                state.isLoading = false;
                state.error = action.payload;
                toast.error("Can't create finance record", tostSetting);
            })
            .addCase(asyncUpdateFinanceAction.fulfilled, (state: FinanceState, action: PayloadAction<IFinance>) => {
                state.isLoading = false;
                state.error = "";
                state.finance = action.payload;
                state.finances = state.finances.map(item =>
                    item.id === action.payload.id ? action.payload : item
                );
                toast.success("Finance record successfully updated", tostSetting);
            })
            .addCase(asyncUpdateFinanceAction.pending, (state: FinanceState) => {
                state.isLoading = true;
            })
            .addCase(asyncUpdateFinanceAction.rejected, (state: FinanceState, action: PayloadAction<any>) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(asyncDeleteFinanceAction.fulfilled, (state: FinanceState, action: PayloadAction<any>) => {
                state.isLoading = false;
                state.error = "";
                state.finances = state.finances.filter((item: IFinance) => item.id !== action.payload.id);
                state.totalFinances = state.totalFinances - 1;
                toast.success("Finance record successfully deleted", tostSetting);
            })
            .addCase(asyncDeleteFinanceAction.pending, (state: FinanceState) => {
                state.isLoading = true;
            })
            .addCase(asyncDeleteFinanceAction.rejected, (state: FinanceState, action: PayloadAction<any>) => {
                state.isLoading = false;
                state.error = action.payload;
                toast.error("Can't delete finance record", tostSetting);
            })
            .addCase(asyncGetFinanceStatsAction.fulfilled, (state: FinanceState, action: PayloadAction<any>) => {
                state.isStatsLoading = false;
                state.error = "";
                state.stats = action.payload;
            })
            .addCase(asyncGetFinanceStatsAction.pending, (state: FinanceState) => {
                state.isStatsLoading = true;
            })
            .addCase(asyncGetFinanceStatsAction.rejected, (state: FinanceState, action: PayloadAction<any>) => {
                state.isStatsLoading = false;
                state.error = action.payload;
            })
            .addCase(asyncGetDailyDealsCountAction.fulfilled, (state: FinanceState, action: PayloadAction<any>) => {
                state.isStatsLoading = false; // Можно использовать тот же флаг загрузки статистики или создать отдельный
                state.error = "";
                state.dailyDealsCount = action.payload;
            })
            .addCase(asyncGetDailyDealsCountAction.pending, (state: FinanceState) => {
                state.isStatsLoading = true;
            })
            .addCase(asyncGetDailyDealsCountAction.rejected, (state: FinanceState, action: PayloadAction<any>) => {
                state.isStatsLoading = false;
                state.error = action.payload;
            });
    }
});

export const { setCurrentPageFinance, resetFinance } = financeSlice.actions;
export default financeSlice.reducer;
